import React, { useState } from "react";
import Page from "@components/Layout/page";
import { WEBPAGE_TITLE } from "@utils/Constants";
import Head from "next/head";
import styles from "./LaufaskolinnPage.module.scss";
import { Leaf } from "@components/Leaf/Leaf";
import { useTranslations } from "next-intl";
import BookMeetingModal from "@components/DiagnosticResults/BookMeetingModal";
import { useInView } from "@hooks/useInView";

export default function LaufaskolinnPage() {
    const t = useTranslations("LeafSchoolPage");
    const [showBookModal, setShowBookModal] = useState(false);

    const [heroRef, heroInView] = useInView();
    const [featuresRef, featuresInView] = useInView();
    const [scheduleRef, scheduleInView] = useInView();
    const [ctaRef, ctaInView] = useInView();

    return (
        <Page fluid bodyClassName={styles.pageBody}>
            <Head>
                <title key="site_title">
                    {"Laufaskólinn - " + WEBPAGE_TITLE}
                </title>
                <meta name="robots" content="noindex" />
            </Head>

            <div className={styles.wrapper}>
                <Leaf className={styles.leafBg1} plain />
                <Leaf className={styles.leafBg2} plain />

                <section
                    ref={heroRef}
                    className={`${styles.hero} ${styles.revealTarget} ${heroInView ? styles.revealed : ""}`}
                >
                    <div className={styles.heroContent}>
                        <span className={styles.badge}>Laufið Education</span>
                        <h1 className={styles.heroTitle}>
                            {t("leaf_education_center")}
                        </h1>
                        <p className={styles.heroSubtitle}>
                            {t("hero_subtitle")}
                        </p>
                        <p className={styles.heroDesc}>{t("hero_desc")}</p>
                        <button
                            className={styles.ctaBtn}
                            onClick={() => setShowBookModal(true)}
                        >
                            {t("book_call")}
                        </button>
                    </div>
                </section>

                <section
                    ref={featuresRef}
                    className={`${styles.features} ${styles.revealTarget} ${featuresInView ? styles.revealed : ""}`}
                >
                    <div className={styles.featureCard}>
                        <div className={styles.featureIcon}>
                            <svg
                                width="28"
                                height="28"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            >
                                <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
                                <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
                            </svg>
                        </div>
                        <h3 className={styles.featureTitle}>
                            {t("feature_courses_title")}
                        </h3>
                        <p className={styles.featureDesc}>
                            {t("feature_courses_desc")}
                        </p>
                    </div>
                    <div className={styles.featureCard}>
                        <div className={styles.featureIcon}>
                            <svg
                                width="28"
                                height="28"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            >
                                <circle cx="12" cy="12" r="10" />
                                <line x1="12" y1="16" x2="12" y2="12" />
                                <line x1="12" y1="8" x2="12.01" y2="8" />
                            </svg>
                        </div>
                        <h3 className={styles.featureTitle}>
                            {t("feature_insights_title")}
                        </h3>
                        <p className={styles.featureDesc}>
                            {t("feature_insights_desc")}
                        </p>
                    </div>
                    <div className={styles.featureCard}>
                        <div className={styles.featureIcon}>
                            <svg
                                width="28"
                                height="28"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            >
                                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                                <polyline points="14 2 14 8 20 8" />
                                <line x1="16" y1="13" x2="8" y2="13" />
                                <line x1="16" y1="17" x2="8" y2="17" />
                                <polyline points="10 9 9 9 8 9" />
                            </svg>
                        </div>
                        <h3 className={styles.featureTitle}>
                            {t("feature_policy_title")}
                        </h3>
                        <p className={styles.featureDesc}>
                            {t("feature_policy_desc")}
                        </p>
                    </div>
                </section>

                <section
                    ref={scheduleRef}
                    className={`${styles.scheduleSection} ${styles.revealTarget} ${scheduleInView ? styles.revealed : ""}`}
                >
                    <h2 className={styles.scheduleTitle}>
                        {t("this_week_schedule")}
                    </h2>

                    <div className={styles.tableWrapper}>
                        <table className={styles.scheduleTable}>
                            <thead>
                                <tr>
                                    <th>{t("time")}</th>
                                    <th>{t("monday")}</th>
                                    <th>{t("tuesday")}</th>
                                    <th>{t("wednesday")}</th>
                                    <th>{t("thursday")}</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <td className={styles.timeCell}>10:00</td>
                                    <td>
                                        <div className={styles.courseChip}>
                                            {t("row2_msg1")} {t("row2_msg2")}
                                            <span className={styles.duration}>
                                                {t("row2_msg3")}
                                            </span>
                                        </div>
                                    </td>
                                    <td>
                                        <div className={styles.courseChip}>
                                            {t("row3_msg1")} {t("row3_msg2")}
                                            <span className={styles.duration}>
                                                {t("row3_msg3")}
                                            </span>
                                        </div>
                                    </td>
                                    <td>
                                        <div className={styles.courseChip}>
                                            {t("row4_msg1")} {t("row4_msg2")}
                                            <span className={styles.duration}>
                                                {t("row4_msg3")}
                                            </span>
                                        </div>
                                    </td>
                                    <td>
                                        <div className={styles.courseChip}>
                                            {t("row5_msg1")} {t("row5_msg2")}
                                            <span className={styles.duration}>
                                                {t("row5_msg3")}
                                            </span>
                                        </div>
                                    </td>
                                </tr>
                                <tr>
                                    <td className={styles.timeCell}>13:00</td>
                                    <td></td>
                                    <td></td>
                                    <td></td>
                                    <td>
                                        <div className={styles.courseChip}>
                                            {t("row6_msg1")} {t("row6_msg2")}
                                        </div>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </section>

                <section
                    ref={ctaRef}
                    className={`${styles.bottomCta} ${styles.revealTarget} ${ctaInView ? styles.revealed : ""}`}
                >
                    <div className={styles.bottomCtaContent}>
                        <h2 className={styles.bottomCtaTitle}>
                            {t("cta_title")}
                        </h2>
                        <p className={styles.bottomCtaDesc}>{t("cta_desc")}</p>
                        <button
                            className={styles.ctaBtnLight}
                            onClick={() => setShowBookModal(true)}
                        >
                            {t("book_call")}
                        </button>
                    </div>
                </section>
            </div>

            {showBookModal && (
                <BookMeetingModal onClose={() => setShowBookModal(false)} />
            )}
        </Page>
    );
}
